import { useState, useEffect } from 'react'

// ─── MILESTONE DEFINITIONS ───────────────────────────────────────────
export const MILESTONES = [
  { id: 'day_1_complete', trigger: 'day_complete', day: 1, emoji: '🌱', title: 'You started.', message: "That's the hardest part. Most people think about doing a job search differently. You're actually doing it." },
  { id: 'active_2', trigger: 'active_days', count: 2, emoji: '✌️', title: 'Two days in.', message: "You're already ahead of most job seekers. Two days of consistent effort is more than most people manage." },
  { id: 'active_5', trigger: 'active_days', count: 5, emoji: '🔥', title: 'Five active days.', message: "Five days of working the system. The foundation is being built. Keep going." },
  { id: 'active_7', trigger: 'active_days', count: 7, emoji: '⭐', title: 'One week.', message: "Seven active days. Your LinkedIn is optimized, your job spec is written, your Hot Ten is identified. The system is working." },
  { id: 'first_pipeline', trigger: 'pipeline_first', emoji: '🎯', title: 'First opportunity added.', message: "Your pipeline is real now. This is where it starts." },
  { id: 'first_hot_ten', trigger: 'hot_ten_first', emoji: '👥', title: 'First advocate identified.', message: "One person who knows your work and wants you to succeed. That person will open a door for you." },
  { id: 'first_call', trigger: 'hot_ten_call', emoji: '📞', title: 'You made the call.', message: "You did something most job seekers never do. You asked for help directly. That is the whole system in one action." },
  { id: 'hot_ten_complete', trigger: 'hot_ten_10', emoji: '🔥', title: 'Hot Ten complete.', message: "Ten people who know your work and want you to win. That network is more powerful than 200 cold applications." },
  { id: 'first_referral', trigger: 'referral_confirmed', emoji: '🤝', title: 'First referral confirmed.', message: "A warm path is open. A referred candidate is hired at 30 times the rate of a cold applicant. This changes everything." },
  { id: 'active_14', trigger: 'active_days', count: 14, emoji: '⭐⭐', title: 'Two weeks of active searching.', message: "Most people abandon their search around week 2. You didn't. Most offers in this system come in weeks 4-8. Keep going." },
  { id: 'active_21', trigger: 'active_days', count: 21, emoji: '⭐⭐⭐', title: 'Three weeks.', message: "This is what consistency looks like. The pipeline is moving. The yes is out there." },
  { id: 'first_interview', trigger: 'interview_scheduled', emoji: '💼', title: 'Interview scheduled.', message: "A recruiter read your resume, liked what they saw, and decided you were worth their time. The system got you in the room." },
  { id: 'active_30', trigger: 'active_days', count: 30, emoji: '🏃', title: 'Thirty active days.', message: "You are in the top percentage of job seekers who actually stick with a system long enough for it to work. The yes is coming." },
  { id: 'offer_received', trigger: 'offer', emoji: '🏆', title: 'YOU GOT THE OFFER.', message: "The system worked. Everything you built — the ikigai, the Hot Ten, the warm paths, the preparation — it all led here. You earned this." },
]

// ─── CONFETTI ────────────────────────────────────────────────────────
function Confetti({ count = 30 }) {
  const pieces = Array.from({ length: count }, (_, i) => ({
    id: i, left: Math.random() * 100,
    delay: Math.random() * 1.5,
    color: ['#00712D', '#C9A84C', '#2563eb', '#f97316', '#7c3aed', '#fff'][Math.floor(Math.random() * 6)]
  }))
  return (
    <>
      {pieces.map(p => (
        <div key={p.id} style={{ position: 'fixed', left: `${p.left}%`, top: '-10px', width: '8px', height: '8px', background: p.color, borderRadius: Math.random() > 0.5 ? '50%' : '2px', animation: `fall 2s ${p.delay}s ease-in forwards`, zIndex: 999, pointerEvents: 'none' }} />
      ))}
    </>
  )
}

// ─── MILESTONE POPUP ─────────────────────────────────────────────────
export function MilestonePopup({ milestone, onClose }) {
  if (!milestone) return null
  const big = ['first_interview', 'offer_received', 'active_30'].includes(milestone.id)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      {big && <Confetti count={40} />}
      <div style={{ background: '#fff', borderRadius: '16px', padding: '2.5rem 2rem', textAlign: 'center', maxWidth: '360px', width: '100%', animation: 'pop 0.3s ease', position: 'relative', zIndex: 501 }}>
        <div style={{ fontSize: '4rem', marginBottom: '0.75rem' }}>{milestone.emoji}</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '0.75rem', lineHeight: 1.2 }}>{milestone.title}</div>
        <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1.75rem' }}>{milestone.message}</p>
        <button className="btn-primary" onClick={onClose} style={{ width: '100%', padding: '0.85rem' }}>Keep going →</button>
      </div>
    </div>
  )
}

// ─── MILESTONE CHECKER HOOK ──────────────────────────────────────────
export function useMilestones(profile, stats, earnedMilestones, onEarn) {
  const [pendingMilestone, setPendingMilestone] = useState(null)

  useEffect(() => {
    if (!profile || !stats) return
    const earned = earnedMilestones || []

    const check = (milestone) => {
      if (earned.includes(milestone.id)) return false
      if (milestone.trigger === 'active_days' && stats.activeDays >= milestone.count) return true
      if (milestone.trigger === 'pipeline_first' && stats.pipeline >= 1) return true
      if (milestone.trigger === 'hot_ten_first' && stats.hotTen >= 1) return true
      if (milestone.trigger === 'hot_ten_10' && stats.hotTen >= 10) return true
      if (milestone.trigger === 'referral_confirmed' && stats.referrals >= 1) return true
      if (milestone.trigger === 'interview_scheduled' && stats.interviews >= 1) return true
      return false
    }

    for (const m of MILESTONES) {
      if (check(m)) {
        setPendingMilestone(m)
        onEarn(m.id)
        break
      }
    }
  }, [stats, earnedMilestones])

  return { pendingMilestone, clearMilestone: () => setPendingMilestone(null) }
}
